import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, Snackbar, Alert, Button, Divider } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

// QR kodu için random değer üretici
function generateRandomQrValue() {
  // Sadece sayı üret (ör: gerçekçi employeeId için)
  return String(Math.floor(100000 + Math.random() * 900000));
}

const API_URL = 'https://pdks.bytebridge.com.tr/api';

function EntryExit() {
  const videoRef = useRef(null);
  const [qrData, setQrData] = useState('');
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(true); // Başlangıçta otomatik başlat
  const codeReader = useRef(null);

  // Demo amaçlı ekranda gözükecek QR kod verisi (her okuma sonrası değişir)
  const [demoQrValue, setDemoQrValue] = useState(generateRandomQrValue());

  // QR kodu okunduğunda çalışır
  const handleScan = async (data) => {
    if (data && data !== qrData) {
      setQrData(data);
      let employeeId = data;
      // Eğer QR kodu JSON ise employeeId'yi parse et
      try {
        const parsed = JSON.parse(data);
        if (parsed.employeeId) employeeId = parsed.employeeId;
      } catch (e) { /* QR düz string ise parse etme */ }
      try {
        // Tüm attendance kayıtlarını çek
        const attRes = await axios.get(`${API_URL}/attendance`);
        const allAttendance = attRes.data.attendance || attRes.data || [];
        // Bu employeeId'ye ait kayıtları filtrele
        const records = allAttendance.filter(r => String(r.EmployeeID) === String(employeeId));
        // Tarihe göre sırala (en son kayıt en başta)
        records.sort((a, b) => new Date(b.EntryTime || b.ExitTime) - new Date(a.EntryTime || a.ExitTime));
        const last = records[0];
        let endpoint = 'entry';
        if (last && last.EntryTime && !last.ExitTime) endpoint = 'exit';
        // Giriş/çıkış kaydı gönder
        const postRes = await axios.post(`${API_URL}/attendance/${endpoint}`, { employeeId });
        setResult(postRes.data.message || (endpoint === 'entry' ? 'Giriş kaydedildi!' : 'Çıkış kaydedildi!'));
        setError('');
        setOpen(true);
        // QR kodunu değiştir
        setDemoQrValue(generateRandomQrValue());
      } catch (err) {
        setResult(null);
        setError(err.response?.data?.message || 'Bir hata oluştu.');
        setOpen(true);
        setDemoQrValue(generateRandomQrValue());
      }
    }
  };

  useEffect(() => {
    if (scanning) {
      codeReader.current = new BrowserMultiFormatReader();
      codeReader.current.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (res, err) => {
          if (res) {
            handleScan(res.getText());
            setScanning(false);
            if (codeReader.current && typeof codeReader.current.reset === 'function') {
              codeReader.current.reset();
            }
          }
          if (err && !(err && err.name === 'NotFoundException')) {
            setError('Kamera/okuma hatası: ' + (err.message || err));
            setOpen(true);
          }
        }
      );
    }
    return () => {
      if (codeReader.current && typeof codeReader.current.reset === 'function') {
        codeReader.current.reset();
      }
    };
    // eslint-disable-next-line
  }, [scanning]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        QR Kod ile Giriş/Çıkış
      </Typography>
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="body1">
            Kameranızı QR koda doğrultun. Her okuma sonrası QR kod değişir.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <video ref={videoRef} style={{ width: 320, height: 240, border: '1px solid #ccc' }} />
          <Button
            variant={scanning ? 'contained' : 'outlined'}
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => setScanning((prev) => !prev)}
          >
            {scanning ? 'Taramayı Durdur' : 'Taramayı Başlat'}
          </Button>
        </Box>
        <Snackbar open={open} autoHideDuration={3500} onClose={() => setOpen(false)}>
          {error ? (
            <Alert severity="error" onClose={() => setOpen(false)}>
              {error}
            </Alert>
          ) : result ? (
            <Alert severity="success" onClose={() => setOpen(false)}>
              {result}
            </Alert>
          ) : null}
        </Snackbar>
      </Paper>
      <Divider sx={{ my: 3 }}>Veya</Divider>
      <Paper elevation={1} sx={{ padding: 2, textAlign: 'center' }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Ekranda Gözüken QR Kod
        </Typography>
        <QRCodeSVG value={demoQrValue} size={180} fgColor="#1a237e" includeMargin={true} />
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          (Demo: {demoQrValue})
        </Typography>
      </Paper>
    </Box>
  );
}

export default EntryExit;
